import {ActionIcon, createStyles, Group, Stack, Tabs, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as DateUtil from 'util/DateUtil';

import RoutineAccessTab from './RoutineAccessTab';
import RoutineForm from './RoutineForm';
import {sanitizeTriggerConfig} from './routineFormValidation';
import RoutineRunsTab from './RoutineRunsTab';
import {
  buildMessageBlocks,
  EventTriggerConfig,
  extractFileBlocks,
  extractPromptFromBlocks,
  FileMessageBlock,
  gqlBlocksToMessageBlocks,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import useAllowedEventTriggers from './useAllowedEventTriggers';
import useRoutine, {RoutineQueryData} from './useRoutine';
import useRoutineFileUpload from './useRoutineFileUpload';
import useRoutineForm, {
  AccessLevel,
  getDefaultEventTriggerConfig,
  getDefaultTimeTriggerConfig,
  RoutineFormState,
  shallowFormEqual,
} from './useRoutineForm';
import useRoutineMutations from './useRoutineMutations';

const DEFAULT_AGENT_TYPE = 'ASSISTANT';

type RoutineDetailTab = 'access' | 'details' | 'runs';

export type DetailPanelMode =
  | {type: 'edit'; routineId: string}
  | {type: 'create'; example?: RoutineFormState; exampleId?: string};

interface RoutineDetailPanelProps {
  mode: DetailPanelMode;
  onClose: () => void;
  onMutationSuccess: () => Promise<void>;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles((theme) => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    padding: theme.spacing.md,
  },
  tabs: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabPanel: {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  runsTabPanel: {
    overflowY: 'auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing.md,
  },
}));

function getDefaultCreateState(timeBasedEnabled: boolean, eventBasedEnabled: boolean): RoutineFormState {
  if (timeBasedEnabled && !eventBasedEnabled) {
    return {
      name: '',
      description: '',
      prompt: '',
      fileBlocks: [],
      triggerType: TriggerType.TIME_BASED,
      triggerConfig: getDefaultTimeTriggerConfig(),
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      memoryEnabled: false,
    };
  }
  if (eventBasedEnabled && !timeBasedEnabled) {
    return {
      name: '',
      description: '',
      prompt: '',
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: getDefaultEventTriggerConfig(),
      allowedApprovalCategories: [],
      agentWorkspaceFolderId: null,
      memoryEnabled: false,
    };
  }
  return {
    name: '',
    description: '',
    prompt: '',
    fileBlocks: [],
    triggerType: null,
    triggerConfig: {},
    allowedApprovalCategories: [],
    agentWorkspaceFolderId: null,
    memoryEnabled: false,
  };
}

function buildFormStateFromQuery(
  data: RoutineQueryData['routine'],
  authorshipEnabled: boolean
): RoutineFormState | null {
  if (data == null) {
    return null;
  }
  const messageBlocks = gqlBlocksToMessageBlocks(data.messageBlocks);
  return {
    name: data.name ?? '',
    description: data.description ?? '',
    prompt: extractPromptFromBlocks(messageBlocks),
    fileBlocks: extractFileBlocks(messageBlocks),
    triggerType: data.triggerType,
    triggerConfig: withDefaultObjectScopes(
      data.triggerConfig as Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>,
      data.triggerType,
      authorshipEnabled
    ),
    allowedApprovalCategories: data.allowedApprovalCategories,
    agentWorkspaceFolderId: data.agentWorkspaceFolder?.id ?? null,
    memoryEnabled: data.memoryEnabled,
  };
}

// Default a prefilled event config with no object_scopes to "viewable" when the section is shown.
// This applies to configs the user did not build from scratch — saved routines on load (which
// predate the feature but are already view-only on the backend) and example prefills — so they stay
// valid/saveable and reflect the backend default. A blank new routine is intentionally left empty
// so the user makes an explicit scope choice.
export function withDefaultObjectScopes(
  triggerConfig: Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>,
  triggerType: TriggerType | null,
  authorshipEnabled: boolean
): Partial<TimeTriggerConfig> | Partial<EventTriggerConfig> {
  if (!authorshipEnabled || triggerType !== TriggerType.EVENT_BASED) {
    return triggerConfig;
  }
  const eventConfig = triggerConfig as Partial<EventTriggerConfig>;
  if ((eventConfig.object_scopes ?? []).length > 0) {
    return triggerConfig;
  }
  return {...eventConfig, object_scopes: ['viewable']};
}

export default function RoutineDetailPanel({
  mode,
  onClose,
  onMutationSuccess,
  timeBasedEnabled,
  eventBasedEnabled,
}: RoutineDetailPanelProps): JSX.Element {
  const {classes, cx, theme} = useStyles();
  const {llmEnableRoutineConfigAuthorship, llmEnableRoutineHomepage, llmEnableRoutinesSharing} =
    useRolloutFlags();
  const authorshipEnabled = llmEnableRoutineConfigAuthorship === true;
  const runsTabEnabled = llmEnableRoutineHomepage !== true;
  const isEdit = mode.type === 'edit';
  const routineId = isEdit ? mode.routineId : null;
  const [activeTab, setActiveTab] = useState<RoutineDetailTab>('details');
  const renderedActiveTab = !runsTabEnabled && activeTab === 'runs' ? 'details' : activeTab;

  useEffect(() => {
    if (!runsTabEnabled && activeTab === 'runs') {
      setActiveTab('details');
    }
  }, [activeTab, runsTabEnabled]);

  const {
    routine: routineData,
    isLoading,
    refetch,
  } = useRoutine({
    routineId,
    enabled: isEdit,
  });

  const defaultCreateState = useMemo(
    () => getDefaultCreateState(timeBasedEnabled, eventBasedEnabled),
    [timeBasedEnabled, eventBasedEnabled]
  );

  const initialFormState = useMemo(() => {
    if (!isEdit) {
      if (mode.type === 'create' && mode.example != null) {
        // Examples are ready-to-save prefills, so default their scope like a loaded routine.
        return {
          ...mode.example,
          triggerConfig: withDefaultObjectScopes(
            mode.example.triggerConfig,
            mode.example.triggerType,
            authorshipEnabled
          ),
        };
      }
      return defaultCreateState;
    }
    return buildFormStateFromQuery(routineData, authorshipEnabled) ?? defaultCreateState;
  }, [isEdit, mode, routineData, defaultCreateState, authorshipEnabled]);

  const {formState, dispatch, isDirty, reset} = useRoutineForm(initialFormState);

  // Re-sync from the server only when the derived state changes by value. Apollo can re-emit
  // `routineData` as a new reference holding stale fields (e.g. the post-save list refetch touches
  // the shared cache entity before this routine's refetch lands), so resetting on every reference
  // change would briefly override the user's edits with the stale value (a visible flicker).
  const lastAppliedServerStateRef = useRef<RoutineFormState | null>(null);
  useEffect(() => {
    if (isEdit && routineData != null) {
      const newState = buildFormStateFromQuery(routineData, authorshipEnabled);
      if (
        newState != null &&
        (lastAppliedServerStateRef.current == null ||
          !shallowFormEqual(newState, lastAppliedServerStateRef.current))
      ) {
        lastAppliedServerStateRef.current = newState;
        reset(newState);
      }
    }
  }, [isEdit, routineData, reset, authorshipEnabled]);

  const {triggers: allowedEventTriggers} = useAllowedEventTriggers({enabled: eventBasedEnabled});

  const mutations = useRoutineMutations(onMutationSuccess);

  const handleNameChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_NAME', value});
    },
    [dispatch]
  );
  const handleDescriptionChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_DESCRIPTION', value});
    },
    [dispatch]
  );
  const handlePromptChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_PROMPT', value});
    },
    [dispatch]
  );
  const handleRemoveFileBlock = useCallback(
    (index: number) => {
      dispatch({type: 'REMOVE_FILE_BLOCK', index});
    },
    [dispatch]
  );
  const handleFileUploaded = useCallback(
    (fileBlock: FileMessageBlock) => {
      dispatch({type: 'ADD_FILE_BLOCK', fileBlock});
    },
    [dispatch]
  );
  const {uploadFile, isUploading} = useRoutineFileUpload(handleFileUploaded);
  const handleTriggerTypeChange = useCallback(
    (value: TriggerType) => {
      dispatch({type: 'SET_TRIGGER_TYPE', value});
    },
    [dispatch]
  );
  const handleTriggerConfigChange = useCallback(
    (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => {
      dispatch({type: 'UPDATE_TRIGGER_CONFIG', updates});
    },
    [dispatch]
  );
  const handleAccessLevelChange = useCallback(
    (value: AccessLevel) => {
      dispatch({type: 'SET_ACCESS_LEVEL', value});
    },
    [dispatch]
  );
  const handleAgentWorkspaceFolderChange = useCallback(
    (value: string | null) => {
      dispatch({type: 'SET_AGENT_WORKSPACE_FOLDER', value});
    },
    [dispatch]
  );
  const handleMemoryEnabledChange = useCallback(
    (value: boolean) => {
      dispatch({type: 'SET_MEMORY_ENABLED', value});
    },
    [dispatch]
  );

  const handleSave = useCallback(async () => {
    if (formState.triggerType == null) {
      return;
    }
    const messageBlocks = buildMessageBlocks(formState.prompt, formState.fileBlocks);
    const triggerConfig = sanitizeTriggerConfig(formState.triggerType, formState.triggerConfig);
    // The empty-string folder marker is a UI-only "folder limit on, folder pending" state that save
    // validation blocks; normalize it to null so it never reaches the backend.
    const agentWorkspaceFolderId =
      formState.agentWorkspaceFolderId === '' ? null : formState.agentWorkspaceFolderId;
    if (isEdit && routineId != null) {
      const success = await mutations.updateRoutine(routineId, {
        name: formState.name.trim(),
        description: formState.description,
        messageBlocks,
        triggerConfig,
        allowedApprovalCategories: formState.allowedApprovalCategories,
        agentWorkspaceFolderId,
        memoryEnabled: formState.memoryEnabled,
      });
      if (success) {
        await refetch();
      }
    } else {
      const newId = await mutations.createRoutine({
        name: formState.name.trim(),
        description: formState.description || undefined,
        messageBlocks,
        agentType: DEFAULT_AGENT_TYPE,
        triggerType: formState.triggerType,
        triggerConfig,
        allowedApprovalCategories: formState.allowedApprovalCategories,
        agentWorkspaceFolderId,
        memoryEnabled: formState.memoryEnabled,
      });
      if (newId != null) {
        onClose();
      }
    }
  }, [isEdit, routineId, formState, mutations, refetch, onClose]);

  const handlePause = useCallback(async () => {
    if (routineId != null) {
      await mutations.pauseRoutine(routineId);
      await refetch();
    }
  }, [routineId, mutations, refetch]);

  const handleResume = useCallback(async () => {
    if (routineId != null) {
      await mutations.resumeRoutine(routineId);
      await refetch();
    }
  }, [routineId, mutations, refetch]);

  const handleArchive = useCallback(async () => {
    if (routineId != null) {
      const success = await mutations.archiveRoutine(routineId, 'Retired');
      if (success) {
        onClose();
      }
    }
  }, [routineId, mutations, onClose]);

  const handleViewAuditLog = useCallback(() => {
    if (routineId != null) {
      exportAuditLogs([routineId]);
    }
  }, [routineId]);

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <Text size="sm" color="dimmed">
          Loading...
        </Text>
      </div>
    );
  }

  const routineForm = (
    <RoutineForm
      isEdit={isEdit}
      formState={formState}
      onNameChange={handleNameChange}
      onDescriptionChange={handleDescriptionChange}
      onPromptChange={handlePromptChange}
      onRemoveFileBlock={handleRemoveFileBlock}
      onAddFile={uploadFile}
      isUploading={isUploading}
      onTriggerTypeChange={handleTriggerTypeChange}
      onTriggerConfigChange={handleTriggerConfigChange}
      onAccessLevelChange={handleAccessLevelChange}
      onAgentWorkspaceFolderChange={handleAgentWorkspaceFolderChange}
      onMemoryEnabledChange={handleMemoryEnabledChange}
      isDirty={isDirty}
      isSaving={mutations.isSaving}
      error={mutations.error}
      onSave={handleSave}
      onCancel={onClose}
      onPause={handlePause}
      onResume={handleResume}
      onArchive={handleArchive}
      onViewAuditLog={handleViewAuditLog}
      currentStatus={routineData?.status}
      allowedEventTriggers={allowedEventTriggers}
      timeBasedEnabled={timeBasedEnabled}
      eventBasedEnabled={eventBasedEnabled}
      permissions={routineData?.permissions}
    />
  );

  // Create mode has no access controls or runs, so it keeps the plain form.
  if (!isEdit || routineId == null) {
    return <div className={classes.panel}>{routineForm}</div>;
  }

  return (
    <Stack className={classes.panel} spacing="md">
      <Group spacing="xs">
        <ActionIcon aria-label="Back to Agents" onClick={onClose} size="sm" variant="ghost">
          <BenchlingIcon icon="chevron-left" />
        </ActionIcon>
        <Text size="lg" fw={600}>
          Agents
        </Text>
      </Group>

      <Stack spacing={2}>
        <Text size="xl" fw={600}>
          {formState.name}
        </Text>
        {routineData?.createdAt != null && (
          <Text size="xs" c="dimmed">
            Created {DateUtil.readableTime(routineData.createdAt, {showTime: false})}
          </Text>
        )}
      </Stack>

      <Tabs
        className={classes.tabs}
        styles={{
          tab: {
            fontSize: theme.fontSizes.sm,
            fontWeight: 500,
            padding: theme.spacing.md,
            '&[data-active]': {
              borderBottomWidth: 3,
            },
          },
        }}
        value={renderedActiveTab}
        onTabChange={(value) => {
          setActiveTab((value as RoutineDetailTab | null) ?? 'details');
        }}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          {llmEnableRoutinesSharing === true && <Tabs.Tab value="access">Manage access</Tabs.Tab>}
          {runsTabEnabled && <Tabs.Tab value="runs">Runs</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel className={classes.tabPanel} value="details" pt="md">
          {routineForm}
        </Tabs.Panel>
        {llmEnableRoutinesSharing === true && (
          <Tabs.Panel className={cx(classes.tabPanel, classes.runsTabPanel)} value="access" pt="md">
            <RoutineAccessTab routineId={routineId} />
          </Tabs.Panel>
        )}
        {runsTabEnabled && (
          <Tabs.Panel className={cx(classes.tabPanel, classes.runsTabPanel)} value="runs" pt="md">
            {formState.triggerType != null && (
              <RoutineRunsTab
                routineId={routineId}
                routineName={formState.name}
                triggerType={formState.triggerType}
              />
            )}
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}
